import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { AlertCircle, CheckCircle2, Info, Loader2, TriangleAlert, X } from 'lucide-react';
import { Button } from './Button';
import { ProgressBar } from './ProgressBar';

/** Kept equal to `TOAST_DURATION_MS`: `components/ui` may not import from `features`. */
const DURATION_MS = 6000;

const TONES = {
  success: { icon: CheckCircle2, iconClass: 'text-ninja-green', accent: 'border-l-ninja-green' },
  info: { icon: Info, iconClass: 'text-info', accent: 'border-l-info' },
  warning: { icon: TriangleAlert, iconClass: 'text-warning', accent: 'border-l-warning' },
  error: { icon: AlertCircle, iconClass: 'text-danger', accent: 'border-l-danger' },
  progress: { icon: Loader2, iconClass: 'text-ninja-green animate-spin', accent: 'border-l-ninja-green' },
};

const ToastItem = ({ toast, onDismiss, onAction }) => {
  const [paused, setPaused] = useState(false);
  const tone = TONES[toast.tone] ?? TONES.info;
  const Icon = tone.icon;
  const urgent = toast.tone === 'error' || toast.tone === 'warning';

  // By id, not by object: a toast replaced in its slot gets a new id and a
  // fresh timer, while a re-render of the same one keeps counting.
  useEffect(() => {
    if (toast.sticky || paused) return undefined;
    const timer = setTimeout(() => onDismiss(toast.id), DURATION_MS);
    return () => clearTimeout(timer);
  }, [toast.id, toast.sticky, paused, onDismiss]);

  return (
    <div
      role={urgent ? 'alert' : 'status'}
      data-testid={toast.testId}
      data-type={toast.tone}
      data-toast="true"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className={clsx(
        'pointer-events-auto flex items-start gap-3 rounded-xl border border-border border-l-4 px-4 py-3',
        'bg-white dark:bg-[#1a1a1a] shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-200',
        tone.accent
      )}
    >
      <Icon size={18} className={clsx('flex-shrink-0 mt-0.5', tone.iconClass)} />

      <div className="flex-1 min-w-0 space-y-1">
        {toast.title && (
          <p className="text-sm font-bold text-ninja-dark dark:text-white">{toast.title}</p>
        )}
        <p className="text-sm text-text-secondary break-words">{toast.message}</p>

        {toast.progress?.total > 0 && (
          <ProgressBar
            size="sm"
            value={toast.progress.done}
            max={toast.progress.total}
            label={`${toast.progress.done} de ${toast.progress.total}`}
          />
        )}

        {toast.action && (
          <Button
            variant="ghost"
            size="xs"
            className="mt-1"
            onClick={() => onAction(toast)}
            data-testid="toast-action"
          >
            {toast.action.label}
          </Button>
        )}
      </div>

      <Button
        variant="subtle"
        size="xs"
        iconOnly
        icon={X}
        aria-label="Cerrar aviso"
        onClick={() => onDismiss(toast.id)}
        data-testid="toast-close"
      />
    </div>
  );
};

/** Whole class names, so Tailwind sees them. */
const PLACEMENTS = {
  bottom: 'bottom-6',
  // Clear of the operator's action bar and of the phone's home indicator.
  aboveActionBar: 'bottom-[calc(6rem_+_env(safe-area-inset-bottom))]',
};

/**
 * Every toast on screen, stacked at the bottom centre above modals and menus.
 *
 * Presentational: `layout/FeedbackHost` reads the store and turns an action's
 * `type` into what it does. Good news closes itself after a few seconds (not
 * while hovered); errors, warnings and progress stay until closed.
 *
 * @param {object} props
 * @param {Array<object>} props.toasts - See `feedbackSlice`.
 * @param {(id: string) => void} props.onDismiss - Should be stable.
 * @param {(toast: object) => void} props.onAction
 * @param {'bottom'|'aboveActionBar'} [props.placement='bottom']
 */
export const ToastStack = ({ toasts, onDismiss, onAction, placement = 'bottom' }) => {
  if (toasts.length === 0) return null;

  // The container lets clicks through: only the cards themselves catch them.
  return createPortal(
    <div
      data-testid="toast-stack"
      data-placement={placement}
      className={clsx(
        'fixed left-1/2 -translate-x-1/2 z-[10050] w-[min(28rem,calc(100vw-2rem))] flex flex-col gap-2 pointer-events-none',
        PLACEMENTS[placement] || PLACEMENTS.bottom
      )}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} onAction={onAction} />
      ))}
    </div>,
    document.body
  );
};
